import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Checkbox, Col, Divider, Form, Input, message, Modal, Row, Spin } from 'antd';
import React from 'react';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CountryInput from '@/components/FormItems/CountryInput';
import EnumSelect from '@/components/FormItems/EnumSelect';
import { MasterServiceType, SystemAppType } from '@/constants';
import AppDatePicker from '@/components/FormItems/DatePicker';
import FeePayingEntities, { parseFeePayingEntitiesData } from './FeeEntityItems/FeePayingEntities';
import FeeSharingEntities, { parseFeeSharingEntitiesData } from './FeeEntityItems/FeeSharingEntities';
import { uniq } from 'lodash';
import { clearAllServiceConfigurationsCache } from '@/hooks/data/masterDataHook';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: (reload?: boolean) => void;
  serviceId?: string;
};
export default function CreateUpdateModal({ serviceId, onClose }: Props) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const clearCache = () => {
    clearAllServiceConfigurationsCache();
  };
  React.useEffect(() => {
    if (serviceId) {
      setLoading(true);
      apiSdk.ServiceConfigurationService.getEntityForUpdate({
        id: serviceId,
      })
        .then(res => {
          form.setFieldsValue({
            ...res,
            feePayingEntities: parseFeePayingEntitiesData(res.feePayingEntities || []),
            feeSharingEntities: parseFeeSharingEntitiesData(res.feeSharingEntities || []),
            feePaying: uniq(res.feePayingEntities?.map(x => x.serviceFeePayingEntityType)),
            feeSharing: uniq(res.feeSharingEntities?.map(x => x.serviceFeeSharingEntityType)),
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [serviceId]);

  const onSubmit = () => {
    form
      .validateFields()
      .then(values => {
        setLoading(true);
        (serviceId
          ? apiSdk.ServiceConfigurationService.update({
              body: values,
            })
          : apiSdk.ServiceConfigurationService.create({
              body: values,
            })
        )
          .then(response => {
            if (response?.success) {
              onClose(true);
              clearCache();
            } else {
              if (response.message) {
                message.error(t(response.message || ''));
              }
            }
          })
          .catch(error => {
            message.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {});
  };
  const availableApps: number[] = Form.useWatch('availableApps', form);

  return (
    <Modal
      style={{ top: 10 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={!serviceId ? t('servicePages_CreateService') : t('servicePages_UpdateService')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={onSubmit}
    >
      <Spin spinning={loading}>
        <Form form={form} size="middle" layout="vertical">
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={ROW_GUTTER}>
            <Col md={7} style={{ borderRadius: '2px solid #1c6f97' }}>
              <Form.Item label={t('servicePages_SearchText')} name="serviceName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <CountryInput />
              </Form.Item>
              <Form.Item
                label={t('servicePages_MasterServiceType')}
                name="masterServiceType"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <EnumSelect enumData={MasterServiceType} />
              </Form.Item>
              <Form.Item label={t('servicePages_ActivationDate')} name="activationDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <AppDatePicker className="w-100" />
              </Form.Item>
              <Form.Item valuePropName="checked" name="isEnabled">
                <Checkbox>{t('base.Enabled')}</Checkbox>
              </Form.Item>
              <Form.Item label={t('base.Description')} name="description">
                <Input.TextArea />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={16}>
              <Form.Item label={t('servicePages_AvailableApps')} name="availableApps">
                <Checkbox.Group>
                  {SystemAppType.__getKeys().map(app => {
                    return (
                      <Checkbox key={app} value={SystemAppType[app]}>
                        {app}
                      </Checkbox>
                    );
                  })}
                </Checkbox.Group>
              </Form.Item>
              <FeePayingEntities availableApps={availableApps} form={form} />

              <Divider />
              <FeeSharingEntities availableApps={availableApps} form={form} />
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
